import { useEffect, useMemo, useRef, useState } from 'react';
import type { EstimateAlgorithm, Floor, StoredReport } from '@wrp/shared';
import { isLabelGroundTruth } from '@wrp/shared';
import * as api from '../api';
import { useDialogs } from '../components/Dialogs';
import { FloorMap } from '../components/FloorMap';
import type { MapSample } from '../components/FloorMap';
import { Icon, IconButton } from '../components/Icon';
import { formatTime } from '../format';
import type { Point } from '../format';
import { clampToFloor, roundPoint } from '../mapUtils';
import { useApp } from '../store';
import { buildEvaluationFile, computePerformance, downloadJson, errorOf, parseEvaluationFile, WITHIN_M } from './evaluation';
import type { EvalState, Sample } from './evaluation';
import { ALGORITHMS, parseNumber, useRecordedReportIds } from './shared';

const CONCURRENCY = 4;
const NO_POINTS: never[] = [];

const round2 = (n: number) => Math.round(n * 100) / 100;

interface EvaluationModeProps {
  floor: Floor;
  reports: StoredReport[];
  state: EvalState;
  onChange: (update: (prev: EvalState) => EvalState) => void;
}

function fileSlug(name: string): string {
  return name.trim().replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'floor';
}

export function EvaluationMode({ floor, reports, state, onChange }: EvaluationModeProps) {
  const { referencePoints, fingerprints, pushToast } = useApp();
  const dialogs = useDialogs();
  const recorded = useRecordedReportIds();
  const fileInput = useRef<HTMLInputElement | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [xText, setXText] = useState('');
  const [yText, setYText] = useState('');

  const { algorithm, k, samples } = state;
  const floorPoints = useMemo(() => referencePoints.filter((rp) => rp.floor_id === floor.id), [referencePoints, floor.id]);

  // ------------------------------------------------------------- estimation
  // Estimates depend on the algorithm settings and on the fingerprint database.
  const key = `${algorithm}|${k}|${fingerprints.length}`;
  const keyRef = useRef(key);
  keyRef.current = key;
  const inFlight = useRef(new Set<string>());

  useEffect(() => {
    // Recorded and labeled reports are survey data, not positioning queries.
    const todo = reports.filter((r) => {
      if (recorded.has(r.id) || (r.ground_truth && isLabelGroundTruth(r.ground_truth))) return false;
      const existing = samples[r.id];
      if (existing && (existing.frozen || existing.key === key)) return false;
      return !inFlight.current.has(`${r.id}|${key}`);
    });
    if (todo.length === 0) return;

    const estimateOne = async (report: StoredReport) => {
      const tag = `${report.id}|${key}`;
      inFlight.current.add(tag);
      try {
        const result = await api.estimate({ report_id: report.id, algorithm, k, floor_id: floor.id, source: 'scan' });
        if (keyRef.current !== key) return;
        onChange((prev) => {
          const existing = prev.samples[report.id];
          if (existing?.frozen) return prev;
          const sample: Sample = {
            report_id: report.id,
            received_at: report.received_at,
            device_id: report.device_id,
            estimate: result.position ? { x: result.position.x, y: result.position.y } : null,
            actual: existing?.actual ?? null,
            frozen: false,
            key,
          };
          return { ...prev, samples: { ...prev.samples, [report.id]: sample } };
        });
      } catch {
        // Retried on the next change of the report feed or settings.
      } finally {
        inFlight.current.delete(tag);
      }
    };

    const queue = [...todo];
    const worker = async () => {
      for (let next = queue.shift(); next; next = queue.shift()) await estimateOne(next);
    };
    for (let i = 0; i < Math.min(CONCURRENCY, queue.length); i++) void worker();
  }, [reports, recorded, samples, key, algorithm, k, floor.id, onChange]);

  // ----------------------------------------------------------------- samples
  const sampleList = useMemo(
    () => Object.values(samples).sort((a, b) => b.received_at.localeCompare(a.received_at)),
    [samples],
  );
  const performance = useMemo(() => computePerformance(sampleList), [sampleList]);
  const mapSamples = useMemo<MapSample[]>(
    () => sampleList.flatMap((s) => (s.estimate ? [{ id: s.report_id, estimate: s.estimate, actual: s.actual }] : [])),
    [sampleList],
  );

  const selected = selectedId ? (samples[selectedId] ?? null) : null;
  const shown: Point | null = selected ? (selected.actual ?? selected.estimate) : null;
  const selectedError = selected ? errorOf(selected) : null;

  useEffect(() => {
    if (!shown) {
      setXText('');
      setYText('');
      return;
    }
    if (parseNumber(xText) !== round2(shown.x)) setXText(String(round2(shown.x)));
    if (parseNumber(yText) !== round2(shown.y)) setYText(String(round2(shown.y)));
    // Only re-sync when the stored value changes, not while the operator is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, shown?.x, shown?.y]);

  const setActual = (id: string, actual: Point | null) =>
    onChange((prev) => {
      const sample = prev.samples[id];
      return sample ? { ...prev, samples: { ...prev.samples, [id]: { ...sample, actual } } } : prev;
    });

  const calibrateTo = (point: Point) => {
    if (!selectedId) return;
    setActual(selectedId, roundPoint(clampToFloor(floor, point)));
  };

  const onTextChange = (nextX: string, nextY: string) => {
    setXText(nextX);
    setYText(nextY);
    const x = parseNumber(nextX);
    const y = parseNumber(nextY);
    if (selectedId && x !== null && y !== null) setActual(selectedId, { x: round2(x), y: round2(y) });
  };

  // -------------------------------------------------------------- save / load
  const save = () => {
    if (sampleList.length === 0) {
      pushToast('info', 'Nothing to save yet');
      return;
    }
    const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '');
    downloadJson(`evaluation-${fileSlug(floor.name)}-${stamp}.json`, buildEvaluationFile(floor, state, sampleList));
    pushToast('success', `Saved ${sampleList.length} samples`);
  };

  const load = async (file: File | undefined) => {
    if (!file) return;
    try {
      const parsed = parseEvaluationFile(await file.text());
      onChange((prev) => ({
        algorithm: parsed.algorithm ?? prev.algorithm,
        k: parsed.k ?? prev.k,
        samples: { ...prev.samples, ...Object.fromEntries(parsed.samples.map((s) => [s.report_id, s])) },
      }));
      const note = parsed.floorId && parsed.floorId !== floor.id ? ` (saved for floor "${parsed.floorName}")` : '';
      pushToast('success', `Loaded ${parsed.samples.length} samples${note}`);
    } catch (error) {
      pushToast('error', error instanceof Error ? error.message : 'Failed to load evaluation');
    }
  };

  const clear = async () => {
    if (!(await dialogs.confirm('Clear all samples and calibrations of this floor?'))) return;
    setSelectedId(null);
    onChange((prev) => ({ ...prev, samples: {} }));
  };

  return (
    <>
      <div className="live__map">
        <FloorMap
          floor={floor}
          referencePoints={NO_POINTS}
          samples={mapSamples}
          selectedSampleId={selectedId}
          onSampleClick={setSelectedId}
          actualPoint={shown}
          onActualMove={selected ? calibrateTo : undefined}
          picking={selected !== null}
          hint={selected ? 'Click the plan or drag the red dot to set the actual position' : 'Click a dot to calibrate its actual position'}
          onMapClick={selected ? calibrateTo : undefined}
        />
      </div>

      <aside className="live__side">
        <section className="box box--auto">
          <header className="box__head">
            <Icon name="target" />
            <h3>Selected report</h3>
            <span className="box__spacer" />
            <IconButton
              icon="check"
              label="Estimate is correct — use it as the actual position"
              disabled={!selected?.estimate}
              onClick={() => selected?.estimate && setActual(selected.report_id, selected.estimate)}
            />
            <IconButton
              icon="close"
              label="Remove the calibration (the red dot returns to the estimate)"
              disabled={!selected?.actual}
              onClick={() => selected && setActual(selected.report_id, null)}
            />
          </header>
          {selected?.estimate ? (
            <>
              <div className="box__body form-grid">
                <label>
                  Estimated X (m)
                  <input type="number" value={selected.estimate.x.toFixed(2)} disabled readOnly />
                </label>
                <label>
                  Estimated Y (m)
                  <input type="number" value={selected.estimate.y.toFixed(2)} disabled readOnly />
                </label>
                <label>
                  Actual X (m)
                  <input type="number" step="0.01" value={xText} onChange={(e) => onTextChange(e.target.value, yText)} />
                </label>
                <label>
                  Actual Y (m)
                  <input type="number" step="0.01" value={yText} onChange={(e) => onTextChange(xText, e.target.value)} />
                </label>
                <label className="form-grid__wide">
                  Snap actual to reference point
                  <select
                    value=""
                    onChange={(e) => {
                      const rp = floorPoints.find((p) => p.id === e.target.value);
                      if (rp) calibrateTo(rp);
                    }}
                  >
                    <option value="">select…</option>
                    {floorPoints.map((rp) => (
                      <option key={rp.id} value={rp.id}>
                        {rp.label} ({rp.x.toFixed(1)}, {rp.y.toFixed(1)})
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <p className="box__note muted">
                {formatTime(selected.received_at)} · {selected.device_id} ·{' '}
                {selectedError !== null ? (
                  <b className={selectedError <= WITHIN_M ? 'text-good' : 'text-bad'}>error {selectedError.toFixed(2)} m</b>
                ) : (
                  'not calibrated yet'
                )}
              </p>
            </>
          ) : (
            <p className="box__empty">
              {selected ? 'No position could be estimated for this report.' : 'Click a dot on the plan or a report below.'}
            </p>
          )}
        </section>

        <section className="box box--auto">
          <header className="box__head">
            <Icon name="ruler" />
            <h3>Performance</h3>
            <span className="box__count">
              {performance?.count ?? 0}/{sampleList.length} calibrated
            </span>
          </header>
          <div className="box__body form-grid">
            <label>
              Algorithm
              <select value={algorithm} onChange={(e) => onChange((prev) => ({ ...prev, algorithm: e.target.value as EstimateAlgorithm }))}>
                {ALGORITHMS.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              k
              <input
                type="number"
                min={1}
                max={50}
                value={k}
                onChange={(e) => onChange((prev) => ({ ...prev, k: Math.max(1, Math.min(50, Number(e.target.value) || 1)) }))}
              />
            </label>
          </div>
          {performance ? (
            <div className="accuracy accuracy--panel">
              <div className={`accuracy__value${performance.mean <= WITHIN_M ? ' is-good' : ' is-bad'}`}>
                {performance.mean.toFixed(2)}
                <small> m mean</small>
              </div>
              <dl className="accuracy__meta">
                <dt>Median</dt>
                <dd>{performance.median.toFixed(2)} m</dd>
                <dt>RMSE</dt>
                <dd>{performance.rmse.toFixed(2)} m</dd>
                <dt>Max</dt>
                <dd>{performance.max.toFixed(2)} m</dd>
                <dt>≤ {WITHIN_M} m</dt>
                <dd>{performance.withinPct.toFixed(0)}%</dd>
              </dl>
            </div>
          ) : (
            <p className="box__note muted">Calibrate at least one report to see the overall accuracy.</p>
          )}
        </section>

        <section className="box box--grow">
          <header className="box__head">
            <Icon name="list" />
            <h3>Reports</h3>
            <span className="box__count">{sampleList.length}</span>
            <span className="box__spacer" />
            <IconButton icon="download" label="Save evaluation to a file" size={14} disabled={sampleList.length === 0} onClick={save} />
            <IconButton icon="upload" label="Load evaluation from a file" size={14} onClick={() => fileInput.current?.click()} />
            <IconButton icon="broom" label="Clear all samples" size={14} danger disabled={sampleList.length === 0} onClick={() => void clear()} />
            <input
              ref={fileInput}
              type="file"
              accept="application/json,.json"
              hidden
              onChange={(e) => {
                void load(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </header>
          <ul className="box__list">
            {sampleList.length === 0 ? <li className="box__empty">Waiting for unlabeled scan reports…</li> : null}
            {sampleList.map((s) => {
              const error = errorOf(s);
              const icon = !s.estimate ? 'close' : s.actual ? 'check' : 'circle';
              const cls = !s.estimate ? 'is-bad' : s.actual ? 'is-done' : 'is-pending';
              return (
                <li key={s.report_id} className={`row${s.report_id === selectedId ? ' is-selected' : ''}`} onClick={() => setSelectedId(s.report_id)}>
                  <span className={`row__status ${cls}`}>
                    <Icon name={icon} size={13} />
                  </span>
                  <span className="row__main">
                    <strong>{s.estimate ? `${s.estimate.x.toFixed(1)}, ${s.estimate.y.toFixed(1)} m` : 'no estimate'}</strong>
                    <span className="muted">
                      {s.received_at ? formatTime(s.received_at) : '—'} · {s.device_id || '—'}
                      {s.frozen ? ' · loaded' : ''}
                    </span>
                  </span>
                  {error !== null ? <span className={`row__value ${error <= WITHIN_M ? 'text-good' : 'text-bad'}`}>{error.toFixed(2)} m</span> : null}
                </li>
              );
            })}
          </ul>
        </section>
      </aside>
    </>
  );
}
